import { type QueryClient } from "@tanstack/react-query";
import { redirect, createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";

import { byocEventsQueryOptions } from "@/api/connectors/byocEvents";
import {
  dragonTileQueryOptions,
  dragonTilesByTypeQueryOptions,
  framesPageQueryOptions,
} from "@/api/dragon-tiles";
import { FRAMES_PAGE_SIZE } from "@/components/TilesPagination/TilesPagination";
import { buildByocHydratedConnector } from "@/features/Connectors/hydrateByocConnector";
import { ParentTilePage } from "@/features/Connectors/ParentTilePage";
import { connectorReturnSearchSchema } from "@/features/DragonTiles/connectorTileSelectReturn";
import { getParentPoolInnerTile } from "@/features/DragonTiles/innerTileUtils";
import {
  hasElevatedAccessValue,
  usePrivilegedModeStore,
} from "@/utils/privilegedMode";

const searchSchema = z
  .object({
    page: z
      .union([z.string(), z.number()])
      .transform((v) => (typeof v === "string" ? Number(v) : v))
      .pipe(z.number().int().positive())
      .catch(1)
      .optional(),
  })
  .merge(connectorReturnSearchSchema);

export const Route = createFileRoute("/_auth/dragon-tiles/$siteId/byoc")({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  loaderDeps: ({ search }) => [search],
  beforeLoad: ({ context, params }) => {
    const privilegedModeDisabled = usePrivilegedModeStore.getState().disabled;
    const hasElevatedAccess = hasElevatedAccessValue(
      context.auth?.user?.user.role,
      privilegedModeDisabled,
    );

    if (!hasElevatedAccess) {
      throw redirect({
        to: "/dragon-tiles/$siteId",
        params: { siteId: params.siteId },
      });
    }
  },
  loader: async ({ params, context, deps: [search] }) => {
    const { queryClient } = context as { queryClient: QueryClient };
    const page = Math.max(1, search.page ?? 1);
    const { siteId } = params;

    const [byocDragonTiles] = await Promise.all([
      queryClient.ensureQueryData(
        dragonTilesByTypeQueryOptions(siteId, "byoc"),
      ),
      // Ensure events are cached before render so the default event selection
      // (initialized once on mount) sees them.
      queryClient.ensureQueryData(byocEventsQueryOptions(siteId)),
    ]);

    if (byocDragonTiles.length === 0) {
      throw redirect({
        to: "/dragon-tiles/$siteId",
        params: { siteId },
      });
    }

    const hydrated = buildByocHydratedConnector({
      byocDragonTiles,
    });

    const dragonTile = await queryClient.ensureQueryData(
      dragonTileQueryOptions(siteId, hydrated.dragonTile.id),
    );
    const parentPoolInnerTileId = getParentPoolInnerTile(dragonTile)?.id;

    await queryClient.ensureQueryData(
      framesPageQueryOptions(siteId, page, FRAMES_PAGE_SIZE, {
        dragon_tile: dragonTile.id,
        ...(parentPoolInnerTileId ? { inner_tile: parentPoolInnerTileId } : {}),
        exclude_content_text: true,
      }),
    );
  },
});

function RouteComponent() {
  return <ParentTilePage />;
}
